import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'; // Functional Job
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'; // Desired Outcomes
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'; // Emotional Job
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'; // Social Job
import OfflineBoltOutlinedIcon from '@mui/icons-material/OfflineBoltOutlined'; // Triggers
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'; // Struggles
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useState } from 'react';

// Helper function to get icon and color based on job type
const getJobTypeProps = (jobType) => {
  switch (jobType) {
    case 'functional':
      return { icon: <BuildOutlinedIcon fontSize="small" />, color: 'primary' };
    case 'emotional':
      return { icon: <FavoriteBorderOutlinedIcon fontSize="small" />, color: 'secondary' };
    case 'social':
      return { icon: <GroupOutlinedIcon fontSize="small" />, color: 'info' };
    default:
      return { icon: null, color: 'default' };
  }
};

const JtbdMapAnalysisTranscript = ({ summaryData }) => {
  const [expandedJob, setExpandedJob] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedJob(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Jobs-To-Be-Done Map analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Jobs-To-Be-Done (JTBD) Map
          <AnalysisLensHelperText lensName="jtbd_map" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {results.summary || 'No summary provided.'}
          </Typography>
        </Box>

        {/* Jobs Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Identified Jobs
        </Typography>

        {results.jobs?.length > 0 ? (
          results.jobs.map((jobData, index) => {
            const panelId = `job-panel-${index}`;
            const isExpanded = expandedJob === panelId;
            const jobTypeProps = getJobTypeProps(jobData.job_type);

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded ? 'grey.50' : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Chip
                      icon={jobTypeProps.icon}
                      label={jobData.job_type.charAt(0).toUpperCase() + jobData.job_type.slice(1)}
                      size="small"
                      color={jobTypeProps.color}
                      variant="outlined"
                      sx={{
                        textTransform: 'capitalize',
                        minWidth: '90px',
                        justifyContent: 'flex-start',
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      // sx={{
                      //   fontWeight: 'medium',
                      //   flexGrow: 1,
                      //   width: 120,
                      //   overflow: 'hidden',
                      //   textOverflow: 'ellipsis',
                      //   whiteSpace: 'nowrap',
                      // }}
                    >
                      {jobData.job}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Grid container spacing={3}>
                    {/* Triggers */}
                    {jobData.triggers?.length > 0 && (
                      <Grid item xs={12} md={6} lg={4}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <OfflineBoltOutlinedIcon fontSize="inherit" color="action" /> Triggers
                        </Typography>
                        <List dense disablePadding>
                          {jobData.triggers.map((item, idx) => (
                            <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* Struggles */}
                    {jobData.struggles?.length > 0 && (
                      <Grid item xs={12} md={6} lg={4}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <ReportProblemOutlinedIcon fontSize="inherit" color="warning" /> Struggles
                        </Typography>
                        <List dense disablePadding>
                          {jobData.struggles.map((item, idx) => (
                            <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* Desired Outcomes */}
                    {jobData.desired_outcomes?.length > 0 && (
                      <Grid item xs={12} md={6} lg={4}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <CheckCircleOutlineOutlinedIcon fontSize="inherit" color="success" />{' '}
                          Desired Outcomes
                        </Typography>
                        <List dense disablePadding>
                          {jobData.desired_outcomes.map((item, idx) => (
                            <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* Quotes */}
                    {jobData.quotes?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FormatQuoteIcon fontSize="inherit" color="action" /> Supporting Quotes
                        </Typography>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {jobData.quotes.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 1 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific jobs identified in this analysis.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default JtbdMapAnalysisTranscript;
